import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { format, isBefore, startOfDay } from "date-fns";
import { Wrench, AlertTriangle, CheckCircle2, Clock, Ship, ChevronRight, Calendar, User } from "lucide-react";
import { JOB_STATUS_CONFIG, PRIORITY_CONFIG, StatusBadge, PriorityBadge, JOB_TYPES } from "./PMSConstants";

interface PMSMyJobsProps {
  jobs: any[];
  vessels: any[];
  userId?: string;
  fullName?: string | null;
  onSelectJob: (job: any) => void;
  onStartJob: (job: any) => void;
  onCompleteJob: (job: any) => void;
  onDeferJob: (job: any) => void;
}

function getEffectiveStatus(job: any): string {
  if (job.status === "completed") return "completed";
  if (job.status === "deferred") return "deferred";
  if (job.status === "in_progress") return "in_progress";
  if (job.status === "overdue" || (job.status === "scheduled" && job.next_due_date && isBefore(new Date(job.next_due_date), startOfDay(new Date())))) return "overdue";
  return "scheduled";
}

const PMSMyJobs = ({
  jobs, vessels, userId, fullName, onSelectJob, onStartJob, onCompleteJob, onDeferJob,
}: PMSMyJobsProps) => {
  const myJobs = useMemo(() =>
    jobs.filter((j: any) => j.assigned_to === userId).map(j => ({ ...j, effectiveStatus: getEffectiveStatus(j) })),
    [jobs, userId]
  );

  const overdueJobs = myJobs.filter(j => j.effectiveStatus === "overdue");
  const inProgressJobs = myJobs.filter(j => j.effectiveStatus === "in_progress");
  const scheduledJobs = myJobs.filter(j => j.effectiveStatus === "scheduled");
  const completedJobs = myJobs.filter(j => j.effectiveStatus === "completed");
  const completionRate = myJobs.length > 0 ? Math.round((completedJobs.length / myJobs.length) * 100) : 0;

  // Group non-completed by vessel
  const activeJobs = myJobs.filter(j => j.effectiveStatus !== "completed" && j.effectiveStatus !== "deferred");
  const byVessel = useMemo(() => {
    const map = new Map<string, { vessel: any; jobs: any[] }>();
    activeJobs.forEach(j => {
      const v = vessels.find((v: any) => v.id === j.vessel_id);
      if (!map.has(j.vessel_id)) map.set(j.vessel_id, { vessel: v, jobs: [] });
      map.get(j.vessel_id)!.jobs.push(j);
    });
    return Array.from(map.values());
  }, [activeJobs, vessels]);

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal/20 flex items-center justify-center text-sm font-bold text-teal">
          {fullName?.split(" ").map(n => n[0]).join("").slice(0, 2) || "?"}
        </div>
        <div>
          <h2 className="font-display text-lg">My Assigned Jobs</h2>
          <p className="text-xs text-muted-foreground">{fullName || "—"} · {myJobs.length} total job{myJobs.length !== 1 ? "s" : ""}</p>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Overdue", value: overdueJobs.length, color: overdueJobs.length > 0 ? "text-red-500" : "text-emerald-500", icon: AlertTriangle },
          { label: "In Progress", value: inProgressJobs.length, color: "text-amber-500", icon: Wrench },
          { label: "Scheduled", value: scheduledJobs.length, color: "text-blue-500", icon: Clock },
          { label: "Completed", value: completedJobs.length, color: "text-emerald-500", icon: CheckCircle2 },
          { label: "Completion", value: `${completionRate}%`, color: "text-teal", icon: Calendar },
        ].map(k => (
          <Card key={k.label}>
            <CardContent className="p-3 flex items-center gap-2">
              <k.icon className={`w-5 h-5 ${k.color} shrink-0`} />
              <div>
                <p className={`text-lg font-display ${k.color}`}>{k.value}</p>
                <p className="text-[9px] text-muted-foreground">{k.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Completion Progress */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-display">My Completion Rate</span>
            <span className="text-sm font-bold text-teal">{completionRate}%</span>
          </div>
          <Progress value={completionRate} className="h-3" />
        </CardContent>
      </Card>

      {/* Overdue Alert */}
      {overdueJobs.length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-4 h-4" />
              Overdue ({overdueJobs.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {overdueJobs.map(job => (
              <button key={job.id} className="w-full flex items-center justify-between p-3 rounded-lg bg-red-50 dark:bg-red-500/10 hover:bg-red-100 transition-colors text-left"
                onClick={() => onSelectJob(job)}>
                <div className="min-w-0">
                  <p className="text-xs font-display truncate">{job.title}</p>
                  <p className="text-[10px] text-muted-foreground">{job.job_number} · {job.vessel_name} · Due {job.next_due_date ? format(new Date(job.next_due_date), "dd MMM") : "—"}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <PriorityBadge priority={job.priority} />
                  {job.status === "scheduled" && (
                    <Button size="sm" variant="outline" className="h-6 text-[9px]" onClick={(e) => { e.stopPropagation(); onStartJob(job); }}>Start</Button>
                  )}
                  <ChevronRight className="w-3 h-3 text-muted-foreground" />
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Jobs by Vessel */}
      {byVessel.length === 0 && overdueJobs.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500/30 mx-auto mb-3" />
            <p className="text-muted-foreground font-display">No active jobs assigned to you</p>
            <p className="text-xs text-muted-foreground mt-1">Check the Calendar view for upcoming scheduled maintenance</p>
          </CardContent>
        </Card>
      ) : (
        byVessel.map(({ vessel, jobs: vesselJobs }) => (
          <Card key={vessel?.id || "unknown"}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Ship className="w-4 h-4 text-teal" />
                {vessel?.name || "Unknown Vessel"}
                <Badge variant="outline" className="text-[9px] ml-2">{vesselJobs.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {vesselJobs
                .sort((a, b) => {
                  const order = { overdue: 0, in_progress: 1, scheduled: 2 };
                  return (order[a.effectiveStatus as keyof typeof order] ?? 3) - (order[b.effectiveStatus as keyof typeof order] ?? 3);
                })
                .map(job => {
                  const statusConfig = JOB_STATUS_CONFIG[job.effectiveStatus] || JOB_STATUS_CONFIG.scheduled;
                  return (
                    <button key={job.id} className="w-full flex items-center gap-3 p-3 rounded-lg border hover:shadow-md transition-all text-left"
                      onClick={() => onSelectJob(job)}>
                      <Wrench className={`w-4 h-4 shrink-0 ${job.effectiveStatus === "overdue" ? "text-red-500" : job.effectiveStatus === "in_progress" ? "text-amber-500" : "text-muted-foreground"}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-display truncate">{job.title}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {job.job_number} · {JOB_TYPES.find(t => t.value === job.job_type)?.label || job.job_type}
                          {job.interval_days ? ` · Every ${job.interval_days}d` : ""}
                          {job.next_due_date ? ` · Due ${format(new Date(job.next_due_date), "dd MMM")}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <StatusBadge status={job.effectiveStatus} config={JOB_STATUS_CONFIG} />
                        <PriorityBadge priority={job.priority} />
                        {job.effectiveStatus === "scheduled" && (
                          <Button size="sm" variant="outline" className="h-6 text-[9px]" onClick={(e) => { e.stopPropagation(); onStartJob(job); }}>Start</Button>
                        )}
                        {job.effectiveStatus === "in_progress" && (
                          <Button size="sm" variant="outline" className="h-6 text-[9px] text-emerald-600" onClick={(e) => { e.stopPropagation(); onCompleteJob(job); }}>Complete</Button>
                        )}
                        <ChevronRight className="w-3 h-3 text-muted-foreground" />
                      </div>
                    </button>
                  );
                })}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default PMSMyJobs;
